import {
  CalendarX,
  Clock4,
  PhoneOff,
  CalendarOff,
  Armchair,
  TrendingDown,
  XCircle,
} from "lucide-react";

const CAUSES = [
  "Pacientes que no confirman sus citas.",
  "Pacientes que dicen “creo que sí” o “te aviso”.",
  "Recordatorios genéricos que no generan respuesta clara.",
  "Cancelaciones de última hora.",
  "Huecos que recepción no consigue llenar a tiempo.",
  "Pacientes de tratamientos de alto valor que no acuden.",
  "Falta de seguimiento después de una cancelación.",
  "Listas de espera gestionadas manualmente.",
  "Personal de recepción saturado.",
];

const PROBLEM_CARDS = [
  {
    icon: CalendarX,
    title: "Cita no confirmada",
    text: "El paciente nunca respondió al recordatorio y la clínica no sabe si vendrá.",
  },
  {
    icon: Clock4,
    title: "Cancelación de última hora",
    text: "El aviso llega cuando ya es casi imposible ofrecer ese hueco a otro paciente.",
  },
  {
    icon: PhoneOff,
    title: "Paciente que no responde",
    text: "Llamadas y mensajes sin respuesta clara que consumen tiempo de recepción.",
  },
  {
    icon: CalendarOff,
    title: "Hueco imposible de llenar",
    text: "La lista de espera está en un papel o en la memoria de alguien, y llega tarde.",
  },
  {
    icon: Armchair,
    title: "Hora de sillón perdida",
    text: "Equipo, gabinete y materiales preparados para una cita que nunca ocurre.",
  },
  {
    icon: TrendingDown,
    title: "Ingresos que desaparecen",
    text: "Cada hueco vacío es facturación que no se recupera al final del mes.",
  },
];

export default function Problem() {
  return (
    <section id="problema" className="bg-white py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-widest text-navy-light">
            El problema
          </p>
          <h2
            data-testid="problem-title"
            className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-[2.6rem] lg:leading-[1.15]"
          >
            Cada no-show deja una hora de sillón vacía. Y esa hora no vuelve.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-steel sm:text-lg">
            Las clínicas dentales suelen perder ingresos por causas que se repiten cada
            semana:
          </p>
        </div>

        <ul className="mt-8 grid gap-x-8 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
          {CAUSES.map((cause) => (
            <li key={cause} className="flex items-start gap-2.5 text-sm text-navy">
              <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-navy-light" strokeWidth={2} />
              <span>{cause}</span>
            </li>
          ))}
        </ul>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {PROBLEM_CARDS.map((card) => (
            <article
              key={card.title}
              data-testid={`problem-card-${card.title.toLowerCase().replace(/\s+/g, "-")}`}
              className="group rounded-xl border border-[#E8EDF5] bg-platinum/50 p-6 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-navy/15 hover:bg-white hover:shadow-md"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-navy/[0.06] transition-colors duration-200 group-hover:bg-navy">
                <card.icon
                  className="h-5 w-5 text-navy transition-colors duration-200 group-hover:text-white"
                  strokeWidth={1.8}
                />
              </div>
              <h3 className="mt-4 font-heading text-lg font-bold text-navy">{card.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-steel">{card.text}</p>
            </article>
          ))}
        </div>

        <blockquote
          data-testid="problem-highlight"
          className="mt-14 rounded-xl border-l-4 border-navy bg-platinum p-6 sm:p-8"
        >
          <p className="font-heading text-lg font-semibold leading-relaxed text-navy sm:text-xl">
            “El problema no es solo que un paciente no venga. El problema es que la
            clínica no tiene tiempo ni un sistema inteligente para recuperar ese hueco.”
          </p>
        </blockquote>
      </div>
    </section>
  );
}
